/**
 * Main application component
 *
 * Receives data via props (SSR) or from window.__APP_DATA__ (hydration).
 * Delegates rendering to the active theme's Layout.
 */

import { useEffect } from 'react';
import { Layout } from '@themes';
import type { AppData } from '@/types/app';
import { ClickrefInitializer } from '@/components/atoms/ClickrefInitializer';
import { buildPageTitle } from '@/utils/buildPageTitle';

declare global {
  interface Window {
    __APP_DATA__: AppData;
  }
}

interface AppProps {
  data?: AppData;
}

export default function App({ data: propData }: AppProps) {
  // SSR: data comes from props
  // Client: data comes from window.__APP_DATA__ (injected by the server)
  const data = propData ?? (typeof window !== 'undefined' ? window.__APP_DATA__ : undefined);

  useEffect(() => {
    if (typeof document !== 'undefined' && data) {
      document.title = buildPageTitle(data);
    }
  }, [data]);

  if (!data) {
    return (
      <div className="error">
        <h1>Erreur de chargement</h1>
        <p>Les données ne sont pas disponibles.</p>
      </div>
    );
  }

  return (
    <>
      <ClickrefInitializer />
      <Layout data={data} />
    </>
  );
}
